import * as React from 'react'
import AdvancedAlertModalContainer from '../AlertModals/AdvancedAlertModal/AdvancedAlertModalContainer'
import BasicAlertModalContainer from '../AlertModals/BasicAlertModal/BasicAlertModalContainer'
import FixedTable from '@mulesoft/anypoint-components/lib/FixedTable'
import NewAlertDropdown from '../NewAlertDropdown/NewAlertDropdown'
import TextField from '@mulesoft/anypoint-components/lib/TextField'
import Tooltip from '@mulesoft/anypoint-components/lib/Tooltip'
import Toggle from '@mulesoft/anypoint-components/lib/Toggle'
import Link from '@mulesoft/anypoint-components/lib/Link'
import * as labels from '@mulesoft/monitoring-center-components/lib/utils/labels'
import AlertStateTag from './StateTags/AlertStateTag'
import TimeAgo from 'timeago-react'
import { AdvancedAlert, alertNameSort, AlertsFilters, getResourceTypeLabelFromAlert, getSeverityLabel, resourceTypeSort, severitySort } from './model'
import { AlertsListPageFilters } from './AlertsListPageFilters/AlertsListPageFilters'
import { isAllFilter } from './AlertsListPageFilters/filters'
import { Alert, BasicAlert, isBasicAlert } from '../model'
import { MonitoringToasts } from '../../Toasts/Toasts'
import { Toast } from '../../Toasts/model'
import { Urls } from '../../Sidebar/Urls'
import AlertTypeFilterBanner from '../AlertTypeFilterBanner/AlertTypeFilterBanner'
import './AlertsListPage.css'
import Option from '../../../util/Option'
import { Profile } from '../../../lifecycle/auth'
import { ResourceNotFoundError } from '../../../common/error/ResourceNotFoundError'
import { EmptyStatePageStateOverride } from '../../EmptyStatePage/model'

export type ExternalProps = {
  alertId?: string
}

export type ValueProps = {
  isTitanium: boolean
  shouldShowBanner: boolean
  isBasicAlertsEnabled: boolean
  isAPIBasicAlertsEnabled: boolean
  isBasicAlertsLimitExceeded: boolean
  isAlertTypeFilterEnabled: boolean
  alerts: Alert[]
  filteredAlerts: Alert[]
  loading: boolean
  filters: AlertsFilters & {
    search?: string
  }
  organizationDomain: string
  toasts: Toast[]
  hasEditRole?: boolean
  organizationId: string
  authorization: string
  profile: Option<Profile>
}

export type EventProps = {
  fetchAllAlerts: (firstFetch?: boolean) => void
  fetchAllFilteredAlerts: (filters: AlertsFilters & { search?: string }) => void
  pauseAlert: (alertId: string, state: boolean) => void
  changeExistingAlertByAlertId: (alertId: string) => Promise<any>
  filterChange: (key: string, value: string) => void
  filterWithSearch: (input?: string) => void
  handleRedirectAlertLink: (basicAlertId?: string) => void
  handleOpenBasicAlertModal: () => void
  handleOpenAdvancedAlertModal: () => void
  changeColumnSort: (column: string, ascending: boolean) => void
  redirectTo404Page: (emptyState: EmptyStatePageStateOverride) => void
}

export type Props = ValueProps & ExternalProps

export type AllProps = Props & EventProps

export type State = {
  searchInput?: string
}

class AlertsListPage extends React.Component<AllProps, State> {

  private static alertDisabled(state: string, isLoading: boolean) {
    return ['paused', 'pending'].find(s => s === state) || isLoading
  }

  private undefinedSeverityTooltip = 'Click on the alert name to open the alert and define a severity level.'

  constructor(props: AllProps) {
    super(props)

    this.state = {
      searchInput: props.filters.search
    }

    this.getColumns = this.getColumns.bind(this)

    this.onChangeAlertState = this.onChangeAlertState.bind(this)

    this.onFilterChange = this.onFilterChange.bind(this)
    this.onChangeSearchBarInput = this.onChangeSearchBarInput.bind(this)
    this.onKeyPressSearchBar = this.onKeyPressSearchBar.bind(this)

    this.isFiltering = this.isFiltering.bind(this)
    this.isFiltersBarFiltering = this.isFiltersBarFiltering.bind(this)
    this.fetchMoreAlerts = this.fetchMoreAlerts.bind(this)
    this.changeColumnSort = this.changeColumnSort.bind(this)

    this.getNewAlertDropdownProps = this.getNewAlertDropdownProps.bind(this)
    this.getFiltersProps = this.getFiltersProps.bind(this)
    this.getResolvedAlerts = this.getResolvedAlerts.bind(this)
    this.onHandleOpenBasicAlertModal = this.onHandleOpenBasicAlertModal.bind(this)
  }

  onHandleOpenBasicAlertModal(alertId: string) {
    return this.props.changeExistingAlertByAlertId(alertId)
      .then(() => {
        this.props.handleOpenBasicAlertModal()
      })
      .catch((err) => {
        if (err instanceof ResourceNotFoundError) {
          const emptyStatePageStateOverride: EmptyStatePageStateOverride = {
            emptyStateTitle: 'The alert you are looking for could not be found.',
            redirectLink: {
              label: 'Back to Alerts',
              href: '#/alerts'
            },
          }
          this.props.redirectTo404Page(emptyStatePageStateOverride)
        } else {
          this.props.handleRedirectAlertLink()
        }
      })
  }

  onHandleBasicAlertLink(basicAlert: BasicAlert) {
    if (basicAlert.alertId) this.props.handleRedirectAlertLink(basicAlert.alertId)
  }

  getColumns() {
    return [
      {
        key: 'name',
        name: 'Alert name',
        sort: alertNameSort,
        renderer: (row: AdvancedAlert | BasicAlert) => {
          if (isBasicAlert(row)) {
            const basicAlert = row as BasicAlert
            return (
              <div className={'basic-alert-link-container'}>
                <a
                  data-test-id={`${basicAlert.alertName.toLowerCase().replace(/ /g, '-')}-basic-alert-link`}
                  onClick={this.onHandleBasicAlertLink.bind(this, basicAlert)}
                >
                  {basicAlert.alertName}
                </a>
                {this.props.isTitanium && <span className={'alert-name-type-tag'}>Basic alert</span>}
              </div>
            )
          } else {
            const complexAlert = row as AdvancedAlert
            return (
              <div className={'advanced-alert-link-container'}>
                <Link
                  testId={`alert-link-${complexAlert.alertName}`}
                  className={'alert-name' + (AlertsListPage.alertDisabled(complexAlert.alertState, complexAlert.isLoading) ? ' alert-disabled' : '')}
                  to={
                    `/monitoring/#${Urls.customDashboardPage}/${complexAlert.dashboardSlug}` +
                    `?panelId=${complexAlert.panelId}&fullscreen&edit&tab=alert`}
                >
                  {complexAlert.alertName}
                </Link>
                {this.props.isTitanium && <span className={'alert-name-type-tag'}>Advanced alert</span>}
              </div>
            )
          }
        }
      },
      // {
      //   key: 'resourceType',
      //   name: 'Resource type',
      //   width: 180,
      //   sort: resourceTypeSort,
      //   renderer: (row: AdvancedAlert | BasicAlert) => {
      //     const resourceTypeLabel = getResourceTypeLabelFromAlert(row)
      //     return (
      //       <div className={'alert-resource-type'}>
      //         <span>{resourceTypeLabel}</span>
      //       </div>
      //     )
      //   }
      // },
      {
        key: 'severity',
        name: 'Severity',
        width: 150,
        sort: severitySort,
        renderer: (row: AdvancedAlert | BasicAlert) => {
          const severityLabel = getSeverityLabel(row.severity)
          return (
            <div className={'alert-severity'}>
              {!row.severity &&
                <Tooltip testId={'undefined-alert-severity-tooltip'} content={this.undefinedSeverityTooltip} position={'left'}>
                  <span>{severityLabel}</span>
                </Tooltip>
              }
              {row.severity && <span>{severityLabel}</span>}
            </div>
          )
        }
      },
      {
        key: 'state',
        name: 'State',
        disableSort: true,
        width: 300,
        renderer: (row: any) => {
          if (isBasicAlert(row)) {
            const basicAlert = row as BasicAlert
            const date = basicAlert.newStateDate ? new Date(basicAlert.newStateDate) : undefined

            return (
              <div className={'state-container'}>
                {basicAlert.alertState && <AlertStateTag
                  name={basicAlert.alertState}
                  testId={`alert-state-${basicAlert.alertName.toLowerCase().replace(/ /g, '-')}`}
                />}
                {date && <TimeAgo
                  className={'time-ago'}
                  datetime={date}
                  data-test-id={`alert-time-ago-${basicAlert.alertName.toLowerCase().replace(/ /g, '-')}`}
                />}
              </div>
            )
          } else {
            const complexAlert = row as AdvancedAlert
            return (
              <div className={'state-container'}>
                <AlertStateTag name={complexAlert.alertState} testId={`alert-state-${complexAlert.alertName}`}/>
                <TimeAgo
                  className={'time-ago' + (AlertsListPage.alertDisabled(complexAlert.alertState, complexAlert.isLoading) ? ' alert-disabled' : '')}
                  datetime={new Date(complexAlert.newStateDate)}
                  data-test-id={`alert-time-ago-${complexAlert.alertName}`}
                />
              </div>
            )
          }
        }
      },
      {
        key: 'alertEnable',
        name: '',
        disableSort: true,
        width: 200,
        renderer: (alert: Alert) => {
          const toggle = (
            <Toggle
              id={`tg-${alert.alertId}`}
              checked={alert.alertState !== 'paused'}
              checkedValue={alert.isLoading ? 'Pausing' : 'Enabled'}
              uncheckedValue={alert.isLoading ? 'Enabling' : 'Paused'}
              disabled={alert.alertState === 'pending' || alert.isLoading || !this.props.hasEditRole}
              testId={`toggle-${alert.alertName.toLowerCase().replace(/ /g, '-')}`}
              onChange={this.onChangeAlertState.bind(this, alert)}
            />
          )
          const toggleWithToolTip = !this.props.hasEditRole ? (
            <Tooltip
              content={labels.missingAlertPermissions}
              position="left"
            >
              {toggle}
            </Tooltip>
          ) : toggle
          return (
            <div className={'alert-enabling'}>
              {toggleWithToolTip}
            </div>
          )
        }
      }
    ]
  }

  onChangeAlertState(alert: Alert) {
    if (!(alert.alertState === 'pending' || alert.isLoading) && alert.alertId) {
      this.props.pauseAlert(alert.alertId, alert.alertState !== 'paused')
    }
  }

  onFilterChange(key: string, value: string) {
    this.props.filterChange(key, value)
  }

  onKeyPressSearchBar(event: any) {
    if (event.charCode === 13) {
      this.props.filterWithSearch(this.state.searchInput)
    }
  }

  onChangeSearchBarInput(result: any) {
    this.setState({...this.state, searchInput: result.value})
  }

  isFiltering() {
    return this.isFiltersBarFiltering() || this.state.searchInput
  }

  isFiltersBarFiltering() {
    return !isAllFilter(this.props.filters.state) || !isAllFilter(this.props.filters.severity)
  }

  // method executed when scrolling down the alerts list
  fetchMoreAlerts() {
    if (this.isFiltering()) {
      this.props.fetchAllFilteredAlerts({...this.props.filters, search: this.state.searchInput})
    } else {
      this.props.fetchAllAlerts()
    }
  }

  // method executed when changing the sort of the alerts list
  changeColumnSort(column: string, ascending: boolean) {
    this.props.changeColumnSort(column, ascending)
  }

  getNewAlertDropdownProps() {
    return {
      isTitanium: this.props.isTitanium,
      isBasicAlertsEnabled: this.props.isBasicAlertsEnabled,
      isLandingPage: false,
      className: 'popover-dropdown-menu',
      organizationDomain: this.props.organizationDomain,
      disabled: !this.props.hasEditRole,
      handleOpenBasicAlertModal: this.props.handleOpenBasicAlertModal,
      handleOpenAdvancedAlertModal: this.props.handleOpenAdvancedAlertModal
    }
  }

  getFiltersProps() {
    return {
      filters: this.props.filters,
      onChange: this.onFilterChange,
      isAlertTypeFilterEnabled: this.props.isAlertTypeFilterEnabled
    }
  }

  getResolvedAlerts() {
    const {alerts, filteredAlerts, filters, isAlertTypeFilterEnabled} = this.props
    const isTypeFiltering = isAlertTypeFilterEnabled && filters.type && filteredAlerts.length > 0
    const shouldUseFiltered = this.isFiltersBarFiltering() || filters.search || isTypeFiltering
    return alerts.length > 0 && !shouldUseFiltered ? alerts : filteredAlerts
  }

  componentDidMount() {
    this.props.fetchAllAlerts(true)
    const { alertId } = this.props
    if (alertId) {
      this.onHandleOpenBasicAlertModal(alertId)
    }
  }

  render() {
    const {loading, isBasicAlertsEnabled, toasts, hasEditRole, isAPIBasicAlertsEnabled} = this.props

    const resolvedAlerts = this.getResolvedAlerts()
    const filtersProps = this.getFiltersProps()
    const newAlertDropdownProps = this.getNewAlertDropdownProps()

    const newAlertDropdown = hasEditRole ? <NewAlertDropdown {...newAlertDropdownProps}/> : (
      <Tooltip content={labels.missingAlertPermissions} position={'bottom'} offset={[40, 5]}>
        <NewAlertDropdown {...newAlertDropdownProps}/>
      </Tooltip>)

    return (
      <div className={'alert-container'} data-test-id="alert-container">
        <AlertTypeFilterBanner shouldShowBanner={this.props.shouldShowBanner} />
        <div className={'alert-container-top-row'}>
          {newAlertDropdown}
          <TextField
            type="search"
            className="alert-container-top-row-search-bar"
            placeholder="Search alerts"
            testId="alert-container-top-row-search-bar"
            onChange={this.onChangeSearchBarInput}
            onKeyPress={this.onKeyPressSearchBar}
            value={this.state.searchInput}
          />
        </div>
        <AlertsListPageFilters {...filtersProps}/>
        <div className={'alert-list'}>
          <div className={'alert-table-container'} data-test-id="alerts-table-container">
            <FixedTable
              isLoading={loading}
              columns={this.getColumns()}
              data={resolvedAlerts}
              testId={'alerts-table'}
              onSortChange={this.changeColumnSort}
              onShouldLoadMoreRows={this.fetchMoreAlerts}
            />
          </div>
        </div>
        {isBasicAlertsEnabled && <BasicAlertModalContainer/>}
        {isBasicAlertsEnabled && <MonitoringToasts toasts={toasts}/>}
        <AdvancedAlertModalContainer/>
      </div>
    )
  }
}

export default AlertsListPage
